import { connect } from 'react-redux';
import i18next from 'i18next';
import moment from 'moment';
import React, { useEffect, useState } from 'react';

import auditService from '@services/auditLogs';

import { showErrorMessage } from '@actions/messageconfirmation';

import { getClientInfo } from '@selectors/client';

import { Button } from '@commons/utils/styledLibraryComponents';
import { getTheme } from '@commons/utils/theme';
import { LoadingIcon } from '@commons/Metrics/components/Stats/styledComponents';
import Drawer from '@commons/Drawer';
import HistoryDrawerContent from '@commons/HistoryDrawer/HistoryDrawerContent';
import mixPanelUtils, { ENUM_EVENTS } from '@commons/utils/mixpanel';

import SpinnerIcon from '@assets/icons/new-loader.svg';

import { Container, Subtitle, Title } from './styledComponents';

const getFormattedLastUpdatedText = (inventory) => {
  if (!inventory || !inventory.updatedAt) {
    return i18next.t('STOCKS.STOCKS.FORM_LAST_CHANGE_WITHOUT_DAY');
  }

  const inventoryUpdateDate = moment(inventory.updatedAt);

  if (moment().isSame(inventory.updatedAt, 'day')) {
    return i18next.t('STOCKS.STOCKS.FORM_LAST_CHANGE_SAME_DAY', {
      hours: inventoryUpdateDate.format('HH'),
      separator: i18next.t('GENERAL.DATE_TIME_FORMAT_HOUR_SEPARATOR'),
      minutes: inventoryUpdateDate.format('mm'),
    });
  }

  return i18next.t('STOCKS.STOCKS.FORM_LAST_CHANGE_OTHER_DAY', {
    date: inventoryUpdateDate.format('DD/MM/YYYY'),
    hours: inventoryUpdateDate.format('HH'),
    separator: i18next.t('GENERAL.DATE_TIME_FORMAT_HOUR_SEPARATOR'),
    minutes: inventoryUpdateDate.format('mm'),
  });
};

function StockFormHeaderInfo(props) {
  const {
    inventory,
    reference,
    showErrorMessage,
    user: { id: userId, email: userEmail },
    client: { clientId },
  } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [displayInventoryHistory, setDisplayInventoryHistory] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);

  const theme = getTheme();

  /* USE EFFECT */

  useEffect(() => {
    if (inventory) {
      fetchInventoryHistory(inventory.id);
    }
  }, [inventory]);

  /* METHODS */

  const fetchInventoryHistory = async (inventoryId) => {
    setIsLoading(true);
    try {
      const inventoryAuditLogs = await auditService.getTargetIdAuditLogs(inventoryId);
      setAuditLogs(inventoryAuditLogs);
    } catch {
      showErrorMessage(i18next.t('AUDIT_EVENTS.ERROR_FETCHING'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDrawer = () => {
    if (!inventory) {
      return;
    }

    const shouldDisplayDrawer = !displayInventoryHistory;

    setDisplayInventoryHistory(shouldDisplayDrawer);

    if (shouldDisplayDrawer) {
      mixPanelUtils.sendMetric(ENUM_EVENTS.OPEN_AUDIT_LOG_SIDE_PANEL, {
        userId,
        userEmail,
        clientId,
        path: location.pathname,
        inventoryId: inventory.id,
        inventoryReference: inventory.reference,
      });
    }
  };

  return (
    <>
      <Container>
        <Title>
          {i18next.t('STOCKS.STOCKS.FORM_TYPE_LABEL')} {reference}
        </Title>

        {!inventory && <Subtitle>{getFormattedLastUpdatedText(inventory)}</Subtitle>}
        {isLoading && inventory && (
          <LoadingIcon alt={'component-loader'} height="16px" src={SpinnerIcon} width="16px" />
        )}
        {!isLoading && inventory && (
          <Button
            buttonCustomStyle={{
              paddingLeft: '0px',
              height: '20px',
              font: theme.fonts.textSmallHeight14,
            }}
            color={'inpulse-outline'}
            fontColor={theme.colors.blacks.ipBlack4}
            handleClick={handleOpenDrawer}
            icon={'/images/inpulse/history.svg'}
            iconOnLeft={false}
            label={getFormattedLastUpdatedText(inventory)}
            noBorder
          />
        )}
      </Container>

      <Drawer
        isOpened={displayInventoryHistory}
        overlayOpacity={0}
        onClick={() => {
          setDisplayInventoryHistory(false);
        }}
      >
        <HistoryDrawerContent
          auditLogs={auditLogs}
          subtitle={reference}
          onClose={() => setDisplayInventoryHistory(false)}
        />
      </Drawer>
    </>
  );
}

const mapStateToProps = (state) => ({
  user: state.baseReducer.user,
  client: getClientInfo(state.baseReducer.user),
});

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(StockFormHeaderInfo);
